import { useEffect, useState } from 'react';
import {
  MdCalendarToday,
  MdClose,
  MdFilterList,
  MdPeople,
  MdRefresh,
  MdRemoveRedEye,
  MdSearch,
  MdSubscriptions,
  MdTrendingUp,
  MdWarning
} from 'react-icons/md';
import Modal from '../../components/common/Modal';
import {
  calculateSubscriptionStats,
  subscribeToSubscriptions,
  extendSubscription,
  renewSubscription,
  deleteSubscription,
} from '../../services/subscriptionService';
import { CLASS_NAMES } from '../../utils/constants';

/* ─── Helpers ────────────────────────────────────────────────── */
const STATUS_CONFIG = {
  active: { label: 'Active', bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  expiring: { label: 'Expiring Soon', bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  expired: { label: 'Expired', bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  cancelled: { label: 'Cancelled', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.cancelled;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const formatDate = (d) => {
  if (!d) return 'N/A';
  try {
    const dateObj = d.seconds ? new Date(d.seconds * 1000) : (d.toDate ? d.toDate() : new Date(d));
    if (isNaN(dateObj.getTime())) return 'N/A';
    return dateObj.toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (error) {
    return 'N/A';
  }
};

/* ─── Stat Card ──────────────────────────────────────────────── */
const StatCard = ({ icon: Icon, label, value, sub, iconBg, iconColor }) => (
  <div className="bg-white rounded-xl md:rounded-2xl border border-gray-100 shadow-sm p-3 md:p-4 flex items-center gap-3 hover:shadow-md transition-shadow">
    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
      <Icon className={`text-xl md:text-2xl ${iconColor}`} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] md:text-xs text-gray-500 font-medium mb-0.5 truncate">{label}</p>
      <p className="text-lg md:text-xl lg:text-2xl font-bold text-darkNavy leading-none truncate">{value}</p>
      {sub && <p className="text-[9px] md:text-xs text-gray-400 mt-0.5 truncate">{sub}</p>}
    </div>
  </div>
);

/* ─── Mobile Card ────────────────────────────────────────────── */
const MobileCard = ({ sub, onView }) => (
  <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start gap-3 md:gap-4 p-3 md:p-4">
      {/* Avatar */}
      <div className="w-10 h-10 md:w-12 md:h-12 min-w-[2.5rem] md:min-w-[3rem] rounded-full bg-gradient-to-br from-blue-600 to-emerald-500 flex items-center justify-center text-white font-bold text-base md:text-lg flex-shrink-0">
        {sub.student.charAt(0)}
      </div>

      {/* Name + email + details */}
      <div className="flex-1 min-w-0">
        {/* Name */}
        <h3 className="font-bold text-sm md:text-base text-gray-900 mb-0.5 truncate">
          {sub.student}
        </h3>
        {/* Email */}
        <p className="text-xs md:text-sm text-gray-500 mb-2 truncate">
          {sub.email}
        </p>

        {/* Status Badge + Subject/Class */}
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
          <StatusBadge status={sub.status} />
          <span className="text-xs md:text-sm text-gray-600 font-semibold">
            {sub.subject} • {sub.class}
          </span>
        </div>

        {/* Expiry Date */}
        <p className="text-[10px] md:text-xs text-gray-400 font-medium">
          Exp. {formatDate(sub.expiryDate)}
        </p>
      </div>

      {/* Eye button */}
      <button
        onClick={() => onView(sub)}
        className="w-9 h-9 md:w-10 md:h-10 min-w-[2.25rem] md:min-w-[2.5rem] flex-shrink-0 bg-blue-50 text-primary rounded-lg md:rounded-xl hover:bg-primary hover:text-white transition-all flex items-center justify-center"
        title="View details"
      >
        <MdRemoveRedEye className="text-lg md:text-xl" />
      </button>
    </div>
  </div>
);

/* ─── Desktop Table Row ──────────────────────────────────────── */
const TableRow = ({ sub, onView }) => (
  <tr className="hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0 group">
    <td className="px-5 py-4">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-blue-400 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
          {sub.student.charAt(0)}
        </div>
        <div>
          <p className="font-bold text-gray-900 text-sm group-hover:text-primary transition-colors">{sub.student}</p>
          <p className="text-xs text-gray-500 font-medium">{sub.email}</p>
        </div>
      </div>
    </td>
    <td className="px-5 py-4 text-sm font-semibold text-gray-700">{sub.class}</td>
    <td className="px-5 py-4 text-sm font-bold text-gray-800">{sub.subject}</td>
    <td className="px-5 py-4">
      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold">{sub.plan}</span>
    </td>
    <td className="px-5 py-4 text-sm font-bold text-emerald-600">{sub.amount}</td>
    <td className="px-5 py-4 text-sm font-medium text-gray-600">{formatDate(sub.expiryDate)}</td>
    <td className="px-5 py-4"><StatusBadge status={sub.status} /></td>
    <td className="px-5 py-4 text-right">
      <button onClick={() => onView(sub)} className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-primary hover:text-white transition-all" title="View details">
        <MdRemoveRedEye size={18} />
      </button>
    </td>
  </tr>
);

/* ─── Main Component ─────────────────────────────────────────── */
const Subscriptions = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    expired: 0,
    expiring: 0,
    cancelled: 0,
    totalRevenue: 0,
  });

  // Action states for extending and renewing subscriptions
  const [actionType, setActionType] = useState(null); // 'extend' | 'renew' | null
  const [extendDays, setExtendDays] = useState('30');
  const [customDays, setCustomDays] = useState('');
  const [renewPlan, setRenewPlan] = useState('monthly');
  const [customRenewDays, setCustomRenewDays] = useState('');
  const [customRenewAmount, setCustomRenewAmount] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  // Subscribe to real-time subscription updates
  useEffect(() => {
    setLoading(true);
    const unsubscribe = subscribeToSubscriptions((data) => {
      setSubscriptions(data);
      setStats(calculateSubscriptionStats(data));
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const counts = {
    all: stats.total,
    active: stats.active,
    expiring: stats.expiring,
    expired: stats.expired,
    cancelled: stats.cancelled,
  };

  // FIX: short tab labels so they all fit without "Cancelled" being hidden
  const ALL_TABS = [
    { id: 'all', label: 'All', count: counts.all },
    { id: 'active', label: 'Active', count: counts.active },
    { id: 'expiring', label: 'Expiring', count: counts.expiring },
    { id: 'expired', label: 'Expired', count: counts.expired },
    { id: 'cancelled', label: 'Cancel', count: counts.cancelled },
  ];

  const filtered = subscriptions.filter((s) => {
    const matchTab = activeTab === 'all' || s.status === activeTab;
    const matchSearch = s.student?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.subject?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchTab && matchSearch;
  });

  const openModal = (sub) => { 
    setSelectedSub(sub); 
    setShowViewModal(true); 
    setActionType(null); 
    setActionError('');
  };
  
  const closeModal = () => { 
    setShowViewModal(false); 
    setSelectedSub(null); 
    setActionType(null); 
    setActionError('');
  };

  const handleExtend = async (e) => {
    e.preventDefault();
    if (!selectedSub) return;
    setActionLoading(true);
    setActionError('');
    
    const days = extendDays === 'custom' ? parseInt(customDays, 10) : parseInt(extendDays, 10);
    if (!days || isNaN(days) || days <= 0) {
      setActionError('Please enter a valid number of days.');
      setActionLoading(false);
      return;
    }
    
    try {
      await extendSubscription(selectedSub.userId, selectedSub.subjectId, days);
      setActionLoading(false);
      alert('Subscription extended successfully!');
      closeModal();
    } catch (err) {
      console.error(err);
      setActionError(err.message || 'Failed to extend subscription. Please try again.');
      setActionLoading(false);
    }
  };

  const handleRenew = async (e) => {
    e.preventDefault();
    if (!selectedSub) return;
    setActionLoading(true);
    setActionError('');
    
    let planName = '';
    let amount = '';
    let days = 0;
    
    if (renewPlan === 'monthly') {
      planName = 'Monthly Plan';
      amount = '1000';
      days = 30;
    } else if (renewPlan === 'termly') {
      planName = 'Termly Plan';
      amount = '3000';
      days = 90;
    } else if (renewPlan === 'yearly') {
      planName = 'Yearly Plan';
      amount = '10000';
      days = 365;
    } else {
      planName = 'Custom Plan';
      amount = customRenewAmount;
      days = parseInt(customRenewDays, 10);
    }
    
    if (!days || isNaN(days) || days <= 0) {
      setActionError('Please enter a valid duration in days.');
      setActionLoading(false);
      return;
    }
    
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) < 0) {
      setActionError('Please enter a valid amount.');
      setActionLoading(false);
      return;
    }
    
    try {
      await renewSubscription(selectedSub.userId, selectedSub.subjectId, planName, amount, days);
      setActionLoading(false);
      alert('Subscription renewed successfully!');
      closeModal();
    } catch (err) {
      console.error(err);
      setActionError(err.message || 'Failed to renew subscription. Please try again.');
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSub) return;
    setActionLoading(true);
    setActionError('');
    
    try {
      await deleteSubscription(selectedSub.userId, selectedSub.subjectId);
      setActionLoading(false);
      alert('Subscription deleted successfully!');
      closeModal();
    } catch (err) {
      console.error(err);
      setActionError(err.message || 'Failed to delete subscription. Please try again.');
      setActionLoading(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    // The real-time listener will automatically refresh the data
    setTimeout(() => setLoading(false), 500);
  };

  return (
    <div className="w-full pb-6 md:pb-10">

      {/* ── Page Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-6">
        <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-darkNavy font-poppins truncate">
            Subscription Management
          </h1>
        </div>
        <p className="text-xs md:text-sm text-gray-500">
          Monitor and control student subscriptions
        </p>
      </div>

      {/* ── Stat Cards ── */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
        <StatCard icon={MdSubscriptions} label="Active Subscriptions" value={stats.active} sub={`${stats.total} total`} iconBg="bg-emerald-50" iconColor="text-emerald-600" />
        <StatCard icon={MdTrendingUp} label="Monthly Revenue" value={`₦${stats.totalRevenue.toLocaleString()}`} sub="From all subs" iconBg="bg-blue-50" iconColor="text-blue-600" />
        <StatCard icon={MdWarning} label="Expiring Soon" value={stats.expiring} sub="Needs attention" iconBg="bg-amber-50" iconColor="text-amber-600" />
        <StatCard icon={MdPeople} label="Expired/Cancelled" value={stats.expired + stats.cancelled} sub={`${stats.expired} expired`} iconBg="bg-purple-50" iconColor="text-purple-600" />
      </div>

      {/* ── Filter Card ── */}
      <div className="bg-white rounded-xl md:rounded-2xl border border-gray-100 shadow-sm mb-4 md:mb-5">

        {/* Tabs — horizontal scroll with colorful non-white pill buttons */}
        <div className="border-b border-gray-100 overflow-x-auto scrollbar-hide p-2.5 md:p-3">
          <div className="flex gap-2.5 min-w-max">
            {ALL_TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              const tabThemes = {
                all: {
                  active: 'bg-indigo-600 text-white border-indigo-700 shadow-sm',
                  inactive: 'bg-indigo-100 text-indigo-900 border-indigo-300 hover:bg-indigo-200',
                  countActive: 'bg-white/30 text-white',
                  countInactive: 'bg-indigo-200 text-indigo-900',
                },
                active: {
                  active: 'bg-green-600 text-white border-green-700 shadow-sm',
                  inactive: 'bg-green-100 text-green-900 border-green-300 hover:bg-green-200',
                  countActive: 'bg-white/30 text-white',
                  countInactive: 'bg-green-200 text-green-900',
                },
                expiring: {
                  active: 'bg-amber-500 text-white border-amber-600 shadow-sm',
                  inactive: 'bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200',
                  countActive: 'bg-white/30 text-white',
                  countInactive: 'bg-amber-200 text-amber-900',
                },
                expired: {
                  active: 'bg-purple-600 text-white border-purple-700 shadow-sm',
                  inactive: 'bg-purple-100 text-purple-900 border-purple-300 hover:bg-purple-200',
                  countActive: 'bg-white/30 text-white',
                  countInactive: 'bg-purple-200 text-purple-900',
                },
                cancelled: {
                  active: 'bg-rose-600 text-white border-rose-700 shadow-sm',
                  inactive: 'bg-rose-100 text-rose-900 border-rose-300 hover:bg-rose-200',
                  countActive: 'bg-white/30 text-white',
                  countInactive: 'bg-rose-200 text-rose-900',
                },
              };
              const theme = tabThemes[tab.id] || tabThemes.all;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3.5 md:px-4 py-2 md:py-2.5 rounded-xl font-bold whitespace-nowrap border transition-all text-xs md:text-sm flex-shrink-0 cursor-pointer ${
                    isActive ? theme.active : theme.inactive
                  }`}
                >
                  {tab.label}
                  <span className={`px-2 py-0.5 rounded-full text-[10px] md:text-xs font-extrabold ${
                    isActive ? theme.countActive : theme.countInactive
                  }`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Search + Filter row */}
        <div className="flex flex-col sm:flex-row gap-3 p-3 md:p-4">
          <div className="relative flex-1">
            <MdSearch className="absolute left-3 md:left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-lg md:text-xl" />
            <input
              type="text"
              placeholder="Search student or subject…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 md:pl-11 pr-10 py-2 md:py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary focus:bg-white transition-all text-xs md:text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <MdClose className="text-base md:text-lg" />
              </button>
            )}
          </div>
          <button className="inline-flex items-center justify-center gap-2 px-4 py-2 md:py-2.5 border border-gray-200 rounded-xl text-gray-600 text-xs md:text-sm font-medium hover:bg-gray-50 transition-colors">
            <MdFilterList className="text-base md:text-lg" />
            Filter
          </button>
        </div>
      </div>

      {/* Results count */}
      <p className="text-xs text-gray-400 mb-3 px-1 uppercase tracking-wide font-semibold">
        Showing <span className="font-bold text-gray-600">{filtered.length}</span> subscription{filtered.length !== 1 ? 's' : ''}
      </p>

      {/* ── Mobile Cards (< lg) ── */}
      <div className="lg:hidden space-y-3">
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <MdSubscriptions className="text-5xl text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 text-sm font-medium">No subscriptions found</p>
          </div>
        ) : (
          filtered.map((sub) => <MobileCard key={sub.id} sub={sub} onView={openModal} />)
        )}
      </div>

      {/* ── Desktop Table (>= lg) ── */}
      <div className="hidden lg:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-16 text-center">
            <MdSubscriptions className="text-6xl text-gray-200 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">No subscriptions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-200">
                  {['Student', 'Class', 'Subject', 'Plan', 'Amount', 'Expiry Date', 'Status', 'Action'].map((h) => (
                    <th key={h} className="px-5 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((sub) => <TableRow key={sub.id} sub={sub} onView={openModal} />)}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50">
          <p className="text-sm font-semibold text-gray-500">Showing all <span className="text-gray-900">{filtered.length}</span> results</p>
          <div className="flex items-center gap-1">
            {[1, 2, 3].map((n) => (
              <button key={n} className={`w-9 h-9 rounded-xl text-sm font-bold transition-all ${n === 1 ? 'bg-primary text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>{n}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ── View Modal ── */}
      <Modal 
        isOpen={showViewModal} 
        onClose={closeModal} 
        title={
          actionType === 'extend' 
            ? 'Extend Subscription' 
            : actionType === 'renew' 
              ? 'Renew Subscription' 
              : actionType === 'delete'
                ? 'Delete Subscription'
                : 'Subscription Details'
        } 
        size="medium"
      >
        {selectedSub && (
          <div className="space-y-5">
            {/* Error Banner */}
            {actionError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-semibold rounded-xl flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-ping" />
                {actionError}
              </div>
            )}

            {actionType === null ? (
              // Details View
              <>
                <div className="flex items-center gap-4 p-4 bg-gray-50 border border-gray-100 rounded-2xl">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-blue-400 flex items-center justify-center text-white font-bold text-2xl flex-shrink-0">
                    {selectedSub.student.charAt(0)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-gray-900 text-lg truncate">{selectedSub.student}</p>
                    <p className="text-sm text-gray-500 truncate mb-1">{selectedSub.email}</p>
                    <StatusBadge status={selectedSub.status} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: MdSubscriptions, label: 'Subject', value: selectedSub.subject },
                    { icon: MdPeople, label: 'Class', value: CLASS_NAMES?.[selectedSub.class] || selectedSub.class },
                    { icon: MdFilterList, label: 'Plan', value: selectedSub.plan },
                    { icon: MdTrendingUp, label: 'Amount', value: selectedSub.amount, highlight: true },
                    { icon: MdCalendarToday, label: 'Start Date', value: formatDate(selectedSub.startDate) },
                    { icon: MdCalendarToday, label: 'Expiry Date', value: formatDate(selectedSub.expiryDate) },
                  ].map(({ icon: Icon, label, value, highlight }) => (
                    <div key={label} className="bg-white border border-gray-100 rounded-2xl p-3 shadow-sm">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Icon className="text-gray-400 text-base flex-shrink-0" />
                        <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider truncate">{label}</span>
                      </div>
                      <p className={`text-sm font-bold ${highlight ? 'text-emerald-600' : 'text-gray-900'}`}>{value}</p>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-100">
                  <button 
                    onClick={() => { setActionType('delete'); }} 
                    className="w-full sm:w-auto px-6 py-3 bg-red-50 text-red-600 border border-red-200 rounded-xl font-bold hover:bg-red-600 hover:text-white transition-colors text-sm shadow-sm sm:mr-auto"
                  >
                    Delete Subscription
                  </button>
                  <button onClick={closeModal} className="w-full sm:w-auto px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-colors text-sm">
                    Close
                  </button>
                  {selectedSub.status === 'active' && (
                    <button 
                      onClick={() => { setActionType('extend'); setExtendDays('30'); setCustomDays(''); }} 
                      className="w-full sm:w-auto px-6 py-3 bg-primary text-white rounded-xl font-bold hover:bg-blue-700 transition-colors text-sm shadow-md"
                    >
                      Extend Subscription
                    </button>
                  )}
                  {(selectedSub.status === 'expired' || selectedSub.status === 'expiring' || selectedSub.status === 'cancelled') && (
                    <button 
                      onClick={() => { setActionType('renew'); setRenewPlan('monthly'); setCustomRenewDays(''); setCustomRenewAmount(''); }} 
                      className="w-full sm:w-auto px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors text-sm shadow-md"
                    >
                      Renew Subscription
                    </button>
                  )}
                </div>
              </>
            ) : actionType === 'extend' ? (
              // Extend Form View
              <form onSubmit={handleExtend} className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-blue-50/50 border border-blue-100 rounded-2xl">
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center text-primary flex-shrink-0">
                    <MdCalendarToday size={20} />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Extend Access For</p>
                    <p className="font-bold text-gray-900 text-sm leading-snug">{selectedSub.student}</p>
                    <p className="text-xs text-gray-500">{selectedSub.subject} • {CLASS_NAMES?.[selectedSub.class] || selectedSub.class}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2">Extension Period</label>
                    <select 
                      value={extendDays} 
                      onChange={(e) => setExtendDays(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    >
                      <option value="7">7 Days (1 Week)</option>
                      <option value="14">14 Days (2 Weeks)</option>
                      <option value="30">30 Days (1 Month)</option>
                      <option value="90">90 Days (1 Term)</option>
                      <option value="365">365 Days (1 Year)</option>
                      <option value="custom">Custom Duration</option>
                    </select>
                  </div>

                  {extendDays === 'custom' && (
                    <div className="animate-fadeIn">
                      <label className="block text-xs font-bold text-gray-700 mb-2">Number of Days</label>
                      <input 
                        type="number" 
                        min="1"
                        placeholder="e.g. 45"
                        value={customDays}
                        onChange={(e) => setCustomDays(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        required
                      />
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-100">
                  <button 
                    type="button" 
                    onClick={() => setActionType(null)} 
                    className="w-full sm:w-auto px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-colors text-sm"
                  >
                    Back to Details
                  </button>
                  <button 
                    type="submit" 
                    disabled={actionLoading}
                    className="w-full sm:w-auto px-6 py-3 bg-primary text-white rounded-xl font-bold hover:bg-blue-700 transition-colors text-sm shadow-md flex items-center justify-center gap-2 disabled:opacity-75 disabled:cursor-not-allowed"
                  >
                    {actionLoading ? 'Extending...' : 'Confirm Extension'}
                  </button>
                </div>
              </form>
            ) : actionType === 'renew' ? (
              // Renew Form View
              <form onSubmit={handleRenew} className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-emerald-50/50 border border-emerald-100 rounded-2xl">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-600 flex-shrink-0">
                    <MdRefresh size={20} className="animate-spin-slow" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Renew Access For</p>
                    <p className="font-bold text-gray-900 text-sm leading-snug">{selectedSub.student}</p>
                    <p className="text-xs text-gray-500">{selectedSub.subject} • {CLASS_NAMES?.[selectedSub.class] || selectedSub.class}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2">Renewal Pricing Plan</label>
                    <select 
                      value={renewPlan} 
                      onChange={(e) => setRenewPlan(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    >
                      <option value="monthly">Monthly Plan (30 Days) - ₦1,000</option>
                      <option value="termly">Termly Plan (90 Days) - ₦3,000</option>
                      <option value="yearly">Yearly Plan (365 Days) - ₦10,000</option>
                      <option value="custom">Custom Pricing / Duration</option>
                    </select>
                  </div>

                  {renewPlan === 'custom' && (
                    <div className="grid grid-cols-2 gap-3 animate-fadeIn">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-2">Duration (Days)</label>
                        <input 
                          type="number" 
                          min="1"
                          placeholder="e.g. 60"
                          value={customRenewDays}
                          onChange={(e) => setCustomRenewDays(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-2">Amount (₦)</label>
                        <input 
                          type="number" 
                          min="0"
                          placeholder="e.g. 2000"
                          value={customRenewAmount}
                          onChange={(e) => setCustomRenewAmount(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                          required
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-100">
                  <button 
                    type="button" 
                    onClick={() => setActionType(null)} 
                    className="w-full sm:w-auto px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-colors text-sm"
                  >
                    Back to Details
                  </button>
                  <button 
                    type="submit" 
                    disabled={actionLoading}
                    className="w-full sm:w-auto px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors text-sm shadow-md flex items-center justify-center gap-2 disabled:opacity-75 disabled:cursor-not-allowed"
                  >
                    {actionLoading ? 'Renewing...' : 'Confirm Renewal'}
                  </button>
                </div>
              </form>
            ) : (
              // Delete Confirmation View
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-2xl">
                  <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center text-red-600 flex-shrink-0">
                    <MdWarning size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Delete Subscription</p>
                    <p className="font-bold text-gray-900 text-sm leading-snug truncate">{selectedSub.student}</p>
                    <p className="text-xs text-gray-500 truncate">{selectedSub.subject} • {CLASS_NAMES?.[selectedSub.class] || selectedSub.class}</p>
                  </div>
                </div>

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-amber-800 text-xs font-semibold">
                  Warning: This action will permanently delete this subject subscription. The student will immediately lose access. This action cannot be undone.
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-100">
                  <button 
                    type="button" 
                    onClick={() => setActionType(null)} 
                    className="w-full sm:w-auto px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    onClick={handleDelete}
                    disabled={actionLoading}
                    className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-colors text-sm shadow-md flex items-center justify-center gap-2 disabled:opacity-75 disabled:cursor-not-allowed"
                  >
                    {actionLoading ? 'Deleting...' : 'Yes, Delete Subscription'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Subscriptions;
